import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
 Container, Section, SectionHeading, Eyebrow, BtnPrimary, BtnGhost, CallBtn, ScheduleBtn,
 TrustStrip, ServiceCard, StatBar, CtaBanner, MapEmbed, Stars, Star,
 CheckCircle2, ShieldCheck, DollarSign, Wrench, ArrowRight, Clock, MapPin,
} from "../components/common";
import { useServices, useBlog, useSite } from "../lib/api";
import { useSeo, ORIGIN } from "../lib/seo";

const HERO_ID = "photo-1676018366904-c083ed678e60";
const heroUrl = (w, q) => `https://images.unsplash.com/${HERO_ID}?w=${w}&q=${q}&auto=format&fm=webp`;
const HERO = heroUrl(1440, 60);
const HERO_SRCSET = `${heroUrl(640, 55)} 640w, ${heroUrl(960, 58)} 960w, ${heroUrl(1440, 60)} 1440w, ${heroUrl(1920, 62)} 1920w`;

const WHY = [
 { icon: CheckCircle2, title: "Honest & Transparent", text: "We show you the problem with photos and give a written estimate before any work. You approve everything, no surprises, no upsells." },
 { icon: ShieldCheck, title: "Nationwide Warranty", text: "Qualifying repairs are backed by a 24-month / 24,000-mile NAPA warranty honored at thousands of shops across the country." },
 { icon: Wrench, title: "Full-Service, All Makes", text: "ASE Master Certified mechanics with dealer-level tools for every vehicle, domestic, import, hybrid, EV and diesel, plus auto & collision body repair and a specialty in luxury and exotic makes like Mercedes-Benz, BMW, Porsche and Tesla." },
 { icon: DollarSign, title: "Fair Pricing & Financing", text: "Big-dealer quality without the big-dealer price. Flexible financing keeps essential repairs within reach." },
];

export default function Home() {
 const { data: services = [] } = useServices();
 const { data: posts = [] } = useBlog();
 const { data: site } = useSite();

 useSeo({
 title: "RPM Auto Repair | Full-Service Auto Mechanic & Body Shop, Riverside CA",
 description: "Full-service ASE Master Certified mechanics in Riverside since 1995. Auto repair, maintenance & collision/body work on all makes, from daily drivers to luxury & exotic. Nationwide warranty. Right off the 215 Fwy. (951) 653-4777.",
 path: "/",
 image: HERO,
 jsonLd: [
 { "@context": "https://schema.org", "@type": "WebSite", name: "RPM Auto Repair", url: ORIGIN,
 potentialAction: { "@type": "SearchAction", target: `${ORIGIN}/faq?q={search_term_string}`, "query-input": "required name=search_term_string" } },
 { "@context": "https://schema.org", "@type": "Organization", name: "RPM Auto Repair", url: ORIGIN, telephone: "+19516534777", foundingDate: "1995" },
 ],
 });

 return (
 <>
 {/* HERO */}
 <section className="relative overflow-hidden">
 <img src={HERO} srcSet={HERO_SRCSET} sizes="100vw" width="1920" height="1080" alt="RPM Auto Repair technician working in the Riverside service bay" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
 <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/40" />
 <div className="absolute inset-0 grain" />
 <Container className="relative py-24 sm:py-32 lg:py-40">
 <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
 <Eyebrow>Riverside · Full-Service Mechanics Since 1995</Eyebrow>
 <h1 className="mt-5 font-display text-4xl font-700 leading-[1.02] text-white sm:text-5xl lg:text-7xl">
 Full-Service Auto<br /><span className="text-primary">Repair & Body Shop</span>
 </h1>
 <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
 From everyday cars, trucks and SUVs to high-end luxury and exotic vehicles, our ASE Master Certified mechanics do it all, auto repair, maintenance and collision & body work, backed by a nationwide warranty. Dealership-quality service without the dealership price.
 </p>
 <div className="mt-9 flex flex-col gap-4 sm:flex-row">
 <ScheduleBtn testid="hero-schedule" label="Schedule Appointment" />
 <CallBtn testid="hero-call" />
 </div>
 <div className="mt-8 flex items-center gap-4">
 <Stars value={4.4} size={18} />
 <span className="text-sm text-white/70"><strong className="text-white">4.4/5</strong> from 112+ Google reviews</span>
 </div>
 </motion.div>
 </Container>
 </section>

 <TrustStrip />

 {/* INTRO + STATS */}
 <Section>
 <Container className="grid items-center gap-12 lg:grid-cols-2">
 <div>
 <Eyebrow>Welcome to RPM Auto Repair</Eyebrow>
 <h2 className="mt-4 font-display text-3xl font-600 leading-tight text-white sm:text-4xl">
 Riverside's Trusted Full-Service Auto Shop
 </h2>
 <p className="mt-5 leading-relaxed text-muted-foreground">
 Since 1995, families and businesses across Riverside County have trusted RPM Auto Repair for one simple reason: we treat you honestly and do the job right. From a quick oil change to a complete engine or transmission rebuild, our ASE Master Certified mechanics use dealer-level tools and factory procedures on every vehicle. When you need a trusted Riverside mechanic, we're right off the 215 freeway on E Alessandro Blvd.
 </p>
 <p className="mt-4 leading-relaxed text-muted-foreground">
 From daily drivers, trucks and SUVs to hybrids, EVs, diesels and fleet vehicles, we service all makes and models, and we're a trusted shop for high-end luxury and exotic vehicles like Mercedes-Benz, BMW, Audi, Porsche and Tesla too. We also handle collision and body repair, and every repair is backed by a nationwide warranty. No jargon, no pressure, just the trusted care your vehicle deserves.
 </p>
 <div className="mt-8 flex flex-wrap gap-4">
 <BtnPrimary to="/about">Our Story <ArrowRight size={16} /></BtnPrimary>
 <BtnGhost to="/services">Browse Services</BtnGhost>
 </div>
 </div>
 <StatBar />
 </Container>
 </Section>

 {/* SERVICES */}
 <Section className="bg-steel/40">
 <Container>
 <SectionHeading eyebrow="What We Do" title="Full-Service Auto Repair & Maintenance"
 subtitle="From routine maintenance to complex diagnostics, our certified team handles it all under one roof." />
 <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
 {services.slice(0, 9).map((s, i) => <ServiceCard key={s.slug} svc={s} index={i} />)}
 </div>
 <div className="mt-10 text-center">
 <BtnPrimary to="/services" testid="view-all-services">View All {services.length} Services <ArrowRight size={16} /></BtnPrimary>
 </div>
 </Container>
 </Section>

 {/* WHY CHOOSE */}
 <Section>
 <Container>
 <SectionHeading center eyebrow="The RPM Difference" title="Why Riverside Drivers Choose Us" />
 <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
 {WHY.map(({ icon: Icon, title, text }, i) => (
 <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
 transition={{ duration: 0.4, delay: i * 0.08 }}
 className="group rounded-xl border border-white/10 bg-steel p-7 transition-colors hover:border-primary/50">
 <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary/15 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
 <Icon size={24} />
 </div>
 <h3 className="mt-5 font-display text-xl text-white">{title}</h3>
 <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
 </motion.div>
 ))}
 </div>
 </Container>
 </Section>

 {/* REVIEWS */}
 <Section className="bg-steel/40">
 <Container>
 <SectionHeading center eyebrow="Real Reviews" title="Trusted by Thousands of Neighbors"
 subtitle="We've earned a 4.4-star reputation across Riverside County by treating every customer like family." />
 <div className="mx-auto mt-8 flex max-w-2xl flex-col items-center gap-4 rounded-2xl border border-white/10 bg-steel p-8 text-center sm:p-10">
 <div className="flex items-baseline gap-3">
 <span className="font-display text-5xl text-white">4.4</span>
 <Stars value={4.4} size={22} />
 </div>
 <p className="text-muted-foreground">Rated 4.4 stars from 112+ verified Google reviews. Been in recently? We'd love to hear how we did.</p>
 <Link to="/review" data-testid="home-leave-review-cta" className="mt-2 flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold tracking-wide text-white transition-colors hover:bg-racing-dark">
 <Star size={16} className="fill-white" /> Leave Us a Review
 </Link>
 </div>
 </Container>
 </Section>

 {/* SERVICE AREA */}
 <Section>
 <Container>
 <SectionHeading center eyebrow="Proudly Local" title="One Location, Serving All of Riverside"
 subtitle="From our shop on East Alessandro Boulevard, right off the 215 freeway in the 92508 area, our ASE-certified mechanics care for drivers within about 20 miles across Riverside, Woodcrest, Moreno Valley, Jurupa Valley, Perris, Norco, Corona, Colton and the nearby communities. We also proudly serve customers in the Murrieta and Temecula area. One trusted shop, honest service, close to home." />
 </Container>
 </Section>

 {/* PERKS */}
 <Section className="bg-steel/40 py-16 sm:py-20">
 <Container>
 <SectionHeading center eyebrow="More Reasons to Choose Us" title="Discounts, Warranty & Extras" />
 <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
 {[
   { icon: DollarSign, title: "10% Discounts", text: "AAA members, active & retired military, and first responders save 10%. Just bring valid ID." },
   { icon: ShieldCheck, title: "Nationwide NAPA Warranty", text: "24 months or 24,000 miles, whichever comes first, on qualifying parts and labor, honored coast to coast." },
   { icon: CheckCircle2, title: "Insurance Accepted", text: "We accept insurance for collision repairs and help coordinate your claim from start to finish." },
   { icon: Wrench, title: "Fleet Accounts Welcome", text: "We accept all fleet services with priority scheduling and preventative maintenance programs." },
   { icon: CheckCircle2, title: "Smog Checks", text: "California smog checks and inspections done fast, plus any repairs needed to pass." },
   { icon: DollarSign, title: "Financing Available", text: "Get essential repairs now and pay over time with flexible, transparent financing." },
 ].map(({ icon: Icon, title, text }, i) => (
   <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
     transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
     className="flex gap-4 rounded-xl border border-white/10 bg-steel p-6">
     <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary"><Icon size={22} /></div>
     <div>
       <h3 className="font-display text-lg text-white">{title}</h3>
       <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{text}</p>
     </div>
   </motion.div>
 ))}
 </div>
 </Container>
 </Section>

 {/* BLOG */}
 <Section className="bg-steel/40">
 <Container>
 <SectionHeading eyebrow="Expert Advice" title="From Our Garage Blog"
 subtitle="Practical, honest car-care guides from our ASE-certified team." />
 <div className="grid gap-6 md:grid-cols-3">
 {posts.slice(0, 3).map((p, i) => (
 <motion.article key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
 transition={{ duration: 0.4, delay: i * 0.08 }}
 className="group overflow-hidden rounded-xl border border-white/10 bg-steel">
 <Link to={`/blog/${p.slug}`}>
 <div className="h-44 overflow-hidden">
 <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover opacity-75 transition-transform duration-500 group-hover:scale-110" />
 </div>
 <div className="p-6">
 <div className="flex items-center gap-2 text-xs tracking-wide text-muted-foreground"><Clock size={13} /> {p.readTime}</div>
 <h3 className="mt-3 font-display text-lg leading-snug text-white group-hover:text-primary">{p.title}</h3>
 <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-3">{p.excerpt}</p>
 </div>
 </Link>
 </motion.article>
 ))}
 </div>
 <div className="mt-10 text-center"><BtnGhost to="/blog">Read the Blog</BtnGhost></div>
 </Container>
 </Section>

 {/* MAP + CONTACT */}
 <Section>
 <Container className="grid items-center gap-10 lg:grid-cols-2">
 <div>
 <Eyebrow>Visit Us</Eyebrow>
 <h2 className="mt-4 font-display text-3xl font-600 text-white sm:text-4xl">Find Us in Riverside</h2>
 <p className="mt-4 leading-relaxed text-muted-foreground">
 Conveniently located right off the 215 freeway on East Alessandro Boulevard, our Riverside mechanics serve all of Riverside County. Stop by for a free estimate or call ahead to schedule, we'll get you in and out with honest, quality service.
 </p>
 <div className="mt-6 space-y-3 text-sm text-white">
 <p className="flex items-center gap-3"><MapPin className="text-primary" size={18} /> 2650 E Alessandro Blvd, Riverside, CA 92508</p>
 <p className="flex items-center gap-3"><Clock className="text-primary" size={18} /> Mon-Fri 8:00-6:00 · Sat 8:00-2:00 · Sun Closed</p>
 </div>
 <div className="mt-7 flex flex-wrap gap-4">
 <ScheduleBtn />
 <CallBtn />
 </div>
 </div>
 <MapEmbed />
 </Container>
 </Section>

 <CtaBanner />
 </>
 );
}
